@if (flow && flow.isOpen) {
  <app-confirm-action-modal
    [title]="title"
    [confirmLabel]="confirmLabel"
    [cancelLabel]="cancelLabel"
    [showCancel]="showCancel"
    busyLabel="Adding..."
    cancelButtonClass="btn-outlined"
    [busy]="flow.busy"
    [confirmDisabled]="confirmDisabled"
    [closeOnOverlayClick]="false"
    [mobileSheet]="false"
    testId="family-members-modal"
    (confirm)="onConfirm()"
    (cancel)="onCancel()"
  >
    <div class="members">
      @if (flow.loading) {
        <p class="members__hint" data-testid="members-loading">Loading...</p>
      }

      @if (flow.loadError) {
        <p class="members__error" role="alert" data-testid="members-load-error">
          {{ flow.loadError }}
        </p>
      }

      @if (context) {
        @switch (flow.step) {
          @case ('add') {
            <p class="members__hint">
              <strong data-testid="members-primary">{{
                flow.subject?.plate || 'This membership'
              }}</strong>
              is the primary. Add vehicles now, or skip and add them later.
              <span class="members__counter" data-testid="members-counter">{{ counter }}</span>
            </p>

            @if (cardText) {
              <p class="members__card" data-testid="members-card">
                New vehicles are charged to the primary's card: {{ cardText }}
              </p>
            } @else {
              <p class="members__warning" data-testid="members-no-card">
                The primary has no saved card, so new vehicles cannot be added. Existing members can
                still join.
              </p>
            }

            @if (flow.seatsLeft > 0) {
              <div class="members__tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  class="members__tab"
                  [class.members__tab--active]="tab === 'new'"
                  [attr.aria-selected]="tab === 'new'"
                  data-testid="members-tab-new"
                  (click)="setTab('new')"
                >
                  New vehicle
                </button>
                <button
                  type="button"
                  role="tab"
                  class="members__tab"
                  [class.members__tab--active]="tab === 'existing'"
                  [attr.aria-selected]="tab === 'existing'"
                  data-testid="members-tab-existing"
                  (click)="setTab('existing')"
                >
                  Existing member
                </button>
              </div>

              @if (tab === 'new') {
                @if (flow.canAddNewVehicle) {
                  <app-family-member-form
                    idPrefix="members"
                    testIdPrefix="members"
                    [model]="form"
                    [errors]="errors"
                    [states]="states"
                    [packages]="packageOptions"
                    [discounts]="discountOptions"
                    [price]="formPrice"
                    priceCaption="Charged now and each month"
                    priceUnit=""
                  />
                  <div class="members__actions">
                    <button
                      type="button"
                      class="family-outline-btn"
                      data-testid="members-add-new"
                      (click)="addNewVehicle()"
                    >
                      Add to list
                    </button>
                  </div>
                }
              } @else {
                <div class="members__search" data-testid="members-existing">
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Search by plate or phone number"
                    aria-label="Search existing members"
                    data-testid="members-search"
                    [(ngModel)]="searchText"
                    (keyup.enter)="runSearch()"
                  />
                  <button
                    type="button"
                    class="family-outline-btn"
                    data-testid="members-search-button"
                    [disabled]="flow.searching"
                    (click)="runSearch()"
                  >
                    {{ flow.searching ? 'Searching...' : 'Search' }}
                  </button>
                </div>
                @if (flow.searched && !flow.candidates.length && !flow.searching) {
                  <p class="members__hint" data-testid="members-no-results">
                    No active membership found at this location.
                  </p>
                }
                <ul class="members__candidates">
                  @for (candidate of flow.candidates; track candidate.subscription_id) {
                    <li
                      class="members__candidate"
                      [class.members__candidate--selected]="
                        selected?.subscription_id === candidate.subscription_id
                      "
                      [class.members__candidate--blocked]="!candidate.eligible"
                      data-testid="members-candidate"
                    >
                      <div class="members__candidate-main">
                        <strong>{{ candidate.plate }}</strong>
                        <span
                          >{{ candidate.customer_name || 'No name' }} ·
                          {{ candidate.package_name }} · {{ money(candidate.price) }}/mo</span
                        >
                        @if (!candidate.eligible && candidate.reason) {
                          <span class="members__candidate-reason" data-testid="members-reason">{{
                            candidate.reason.message
                          }}</span>
                        }
                      </div>
                      @if (candidate.eligible) {
                        <button
                          type="button"
                          class="family-outline-btn"
                          data-testid="members-pick"
                          (click)="pick(candidate)"
                        >
                          Select
                        </button>
                      }
                    </li>
                  }
                </ul>
                @if (selected) {
                  <div class="members__discount-row">
                    <div class="family-discount">
                      <span class="family-discount__label">Discount</span>
                      <mat-select
                        class="form-select family-discount__select"
                        panelClass="family-discount-panel"
                        disableOptionCentering
                        aria-label="Discount"
                        data-testid="members-existing-discount"
                        [(ngModel)]="existingDiscount"
                      >
                        @for (value of presets; track value) {
                          <mat-option [value]="value">{{ discountLabel(value) }}</mat-option>
                        }
                      </mat-select>
                    </div>
                    @if (existingPrice !== null) {
                      <div class="family-price" data-testid="members-existing-price">
                        <span class="family-price__label">From next renewal</span>
                        <span class="family-price__value">{{ money(existingPrice) }}/mo</span>
                        <s class="family-price__was">{{ money(selected.price) }}/mo</s>
                      </div>
                    }
                  </div>
                  <p class="members__hint">Nothing is charged now. They keep their own card.</p>
                  <div class="members__actions">
                    <button
                      type="button"
                      class="family-outline-btn"
                      data-testid="members-add-existing"
                      (click)="addExisting()"
                    >
                      Add to list
                    </button>
                  </div>
                }
              }
            } @else {
              <p class="members__hint" data-testid="members-full">
                This family is full. Remove a vehicle from the list to add another.
              </p>
            }

            @if (flow.drafts.length) {
              <ul class="members__drafts" data-testid="members-drafts">
                @for (item of flow.drafts; track item.localId) {
                  <li class="members__draft">
                    <div class="members__draft-main">
                      <strong>{{ item.label }}</strong>
                      <span>
                        @if (item.chargeNow > 0) {
                          Charged now: {{ money(item.chargeNow) }} ·
                        } @else {
                          Nothing charged now ·
                        }
                        {{ money(item.recurringPrice) }}/mo
                      </span>
                    </div>
                    <button
                      type="button"
                      class="family-link-btn"
                      data-testid="members-remove"
                      [disabled]="flow.busy"
                      (click)="flow.removeDraft(item.localId)"
                    >
                      Remove
                    </button>
                  </li>
                }
              </ul>
              @if (chargedNow > 0) {
                <p class="members__total" data-testid="members-total">
                  {{ money(chargedNow) }} will be charged to the primary's card.
                </p>
              }
              @if (needsCard && !cardText) {
                <p class="members__error" role="alert">
                  Remove the new vehicles: the primary has no saved card to charge.
                </p>
              }
            }

            @if (flow.error) {
              <p class="members__error" role="alert" data-testid="members-error">
                {{ flow.error }}
              </p>
            }
          }

          @case ('otp') {
            <p class="members__hint" data-testid="members-otp-info">
              @if (flow.otpSending) {
                Sending a code to the primary...
              } @else if (flow.otpSent) {
                A code was sent to the primary's phone ending {{ flow.otpPhoneLast4 }}. Ask them for
                it and enter it below. It works for 8 minutes.
              } @else {
                The primary needs to approve this. Send them a code first.
              }
            </p>
            <div class="form-group family-form__field">
              <label for="members-otp">Code from the primary</label>
              <input
                id="members-otp"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="6"
                class="form-control members__otp"
                data-testid="members-otp"
                [class.error]="flow.otpError"
                [(ngModel)]="flow.otpCode"
                (keyup.enter)="flow.canSubmitCode && flow.submit()"
              />
            </div>
            <div class="members__actions members__actions--start">
              <button
                type="button"
                class="family-link-btn"
                data-testid="members-resend"
                [disabled]="flow.otpSending || flow.busy"
                (click)="flow.sendOtp()"
              >
                {{ flow.otpSent ? 'Send a new code' : 'Send code' }}
              </button>
            </div>
            @if (flow.otpError) {
              <p class="members__error" role="alert" data-testid="members-otp-error">
                {{ flow.otpError }}
              </p>
            }
          }

          @case ('done') {
            <p class="members__hint" data-testid="members-summary">{{ summary }}</p>
            <ul class="members__drafts">
              @for (outcome of flow.outcomes; track $index) {
                <li class="members__draft" data-testid="members-outcome">
                  <div class="members__draft-main">
                    <strong>{{ outcome.label }}</strong>
                    <span
                      [class.members__error-text]="outcome.status === 'failed'"
                      [class.members__ok-text]="outcome.status === 'added'"
                    >
                      {{ outcome.message }}
                    </span>
                  </div>
                </li>
              }
            </ul>
          }
        }
      }
    </div>
  </app-confirm-action-modal>
}
